import React, { memo } from 'react'
import { useSiteUrl } from '../../../context/site-url'

import type { StyledSvgComponent } from '../../../types/svg'
import {
    DownArrow,
    IconImage,
    LoginButtonText,
    NavigationItem,
    PopUpBox,
    PopUpBoxIcon,
    PopUpBoxItem,
    PopUpBoxWrapper,
    UserIcon,
    UserMenu,
    UserMenuContainer,
    UserMenuLowerContent,
} from './header-section-styles'
import ExternalLink from './../../common/external-link'
import { HeaderSectionTransport } from './../../../transports/region-landing-page-transport'
import ChevronDownIcon from '../../../assets/icons/chevron-down.svg'
import BookingIcon from '../../../assets/icons/userbar-icons/bookings.svg'
import PersonalDetailsIcon from '../../../assets/icons/userbar-icons/personal-details.svg'
import LogoutIcon from '../../../assets/icons/userbar-icons/log-out.svg'
import UserHeaderIcon from '../../../assets/icons/userbar-icons/user-logo.svg'
import LandlordAreaIcon from '../../../assets/icons/userbar-icons/usermenu-icons/layout.svg'
import LandlordListingsIcon from '../../../assets/icons/userbar-icons/usermenu-icons/megaphone.svg'
import LandlordInvoicesIcon from '../../../assets/icons/userbar-icons/usermenu-icons/file-text.svg'

type Navigation = HeaderSectionTransport['navigationSectionResponse']['navigation']

interface UserMenuDesktopProps {
    navigation: Navigation
    isLoggedIn: boolean
    firstName?: string
    isLandlordRole: boolean
    isGuestRole: boolean
    inquiryCount?: number | null
    popUpBoxVisible: boolean
    togglePopUpBoxVisibility: (visible: boolean) => void
}

const UserMenuDesktop: React.FC<UserMenuDesktopProps> = ({
    navigation,
    isLoggedIn,
    firstName,
    isLandlordRole,
    isGuestRole,
    inquiryCount,
    popUpBoxVisible,
    togglePopUpBoxVisibility,
}) => {
    const { originalUrl } = useSiteUrl()
    const {
        loginButton,
        logoutButton,
        guestProfileButton,
        landlordProfileButton,
        userBookingsButton,
        guestInquiriesButton,
        landlordInquiriesButton,
        landlordListingsButton,
        landlordInvoicesButton,
        landlordAreaButton,
    } = navigation

    if (!isLoggedIn) {
        return (
            <ExternalLink
                href={`${loginButton.url}?target=${originalUrl}`}
                as={NavigationItem}
                noCrawl
                style={{ cursor: 'pointer' }}
            >
                <IconImage as={UserHeaderIcon as StyledSvgComponent} />
                <LoginButtonText>{loginButton.text}</LoginButtonText>
            </ExternalLink>
        )
    }

    return (
        <UserMenuContainer
            onMouseEnter={() => togglePopUpBoxVisibility(true)}
            onMouseLeave={() => togglePopUpBoxVisibility(false)}
        >
            <UserIcon as={UserHeaderIcon as StyledSvgComponent} />

            <UserMenu>
                {firstName}
                <UserMenuLowerContent>
                    <DownArrow as={ChevronDownIcon as StyledSvgComponent} />
                </UserMenuLowerContent>
            </UserMenu>

            {popUpBoxVisible && (
                <PopUpBox>
                    {isLandlordRole && (
                        <PopUpBoxWrapper href={landlordAreaButton.url} noCrawl={landlordAreaButton.noCrawl}>
                            <PopUpBoxIcon as={LandlordAreaIcon as StyledSvgComponent} />
                            <PopUpBoxItem>{landlordAreaButton.text}</PopUpBoxItem>
                        </PopUpBoxWrapper>
                    )}
                    {isGuestRole && (
                        <PopUpBoxWrapper href={guestInquiriesButton.url} noCrawl={guestInquiriesButton.noCrawl}>
                            <PopUpBoxIcon as={BookingIcon as StyledSvgComponent} />
                            <PopUpBoxItem>
                                {guestInquiriesButton.text}
                                {inquiryCount ? ` [${inquiryCount}]` : ''}
                            </PopUpBoxItem>
                        </PopUpBoxWrapper>
                    )}
                    {isLandlordRole && (
                        <PopUpBoxWrapper href={landlordInquiriesButton.url} noCrawl={landlordInquiriesButton.noCrawl}>
                            <PopUpBoxIcon as={BookingIcon as StyledSvgComponent} />
                            <PopUpBoxItem>
                                {landlordInquiriesButton.text}
                                {inquiryCount ? ` [${inquiryCount}]` : ''}
                            </PopUpBoxItem>
                        </PopUpBoxWrapper>
                    )}
                    <PopUpBoxWrapper href={userBookingsButton.url} noCrawl={userBookingsButton.noCrawl}>
                        <PopUpBoxIcon as={BookingIcon as StyledSvgComponent} />
                        <PopUpBoxItem>{userBookingsButton.text}</PopUpBoxItem>
                    </PopUpBoxWrapper>
                    {isLandlordRole && (
                        <PopUpBoxWrapper href={landlordListingsButton.url} noCrawl={landlordListingsButton.noCrawl}>
                            <PopUpBoxIcon as={LandlordListingsIcon as StyledSvgComponent} />
                            <PopUpBoxItem>{landlordListingsButton.text}</PopUpBoxItem>
                        </PopUpBoxWrapper>
                    )}
                    {isLandlordRole && (
                        <PopUpBoxWrapper href={landlordInvoicesButton.url} noCrawl={landlordInvoicesButton.noCrawl}>
                            <PopUpBoxIcon as={LandlordInvoicesIcon as StyledSvgComponent} />
                            <PopUpBoxItem>{landlordInvoicesButton.text}</PopUpBoxItem>
                        </PopUpBoxWrapper>
                    )}
                    {isGuestRole && (
                        <PopUpBoxWrapper href={guestProfileButton.url} noCrawl={guestProfileButton.noCrawl}>
                            <PopUpBoxIcon as={PersonalDetailsIcon as StyledSvgComponent} />
                            <PopUpBoxItem>{guestProfileButton.text}</PopUpBoxItem>
                        </PopUpBoxWrapper>
                    )}
                    {isLandlordRole && (
                        <PopUpBoxWrapper href={landlordProfileButton.url} noCrawl={landlordProfileButton.noCrawl}>
                            <PopUpBoxIcon as={PersonalDetailsIcon as StyledSvgComponent} />
                            <PopUpBoxItem>{landlordProfileButton.text}</PopUpBoxItem>
                        </PopUpBoxWrapper>
                    )}
                    <PopUpBoxWrapper href={logoutButton.url} noCrawl={logoutButton.noCrawl}>
                        <PopUpBoxIcon as={LogoutIcon as StyledSvgComponent} />
                        <PopUpBoxItem>{logoutButton.text}</PopUpBoxItem>
                    </PopUpBoxWrapper>
                </PopUpBox>
            )}
        </UserMenuContainer>
    )
}

export default memo(UserMenuDesktop)
